import { useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";
import {
  transformDbToSalesRequest,
  fetchAndTransformSalesRequest,
} from "~/utils/salesRequestTransform";
import { verifyAllUnverifiedPayments } from "~/utils/paymentVerification";

interface UseOrderActionsProps {
  selectedOrder: SalesRequest;
  setSelectedOrder: (order: SalesRequest | null) => void;
  onOrderStatusChange?: () => void;
}

interface UseOrderActionsReturn {
  markAsTest: () => Promise<void>;
  markAsDelivered: () => Promise<void>;
  revertToInTransit: () => Promise<void>;
  markAsPaid: () => Promise<void>;
  markAsUnpaid: () => Promise<void>;
  handleForceApproveWithoutPaymentValidation: () => Promise<void>;
  handleTransportistaReject: (reason: string) => Promise<void>;
  approveDocument: () => Promise<void>;
  isProcessing: boolean;
}

/**
 * Hook that consolidates order action handlers (status changes, payments, etc.)
 * This hook handles actions that modify the order status or payment state.
 */
export function useOrderActions({
  selectedOrder,
  setSelectedOrder,
  onOrderStatusChange,
}: UseOrderActionsProps): UseOrderActionsReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const profileStore = useProfileStore();

  /**
   * Mark order as test (admin/superadmin only)
   */
  const markAsTest = async () => {
    if (!selectedOrder) return;

    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "test",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Status history is recorded by the record_sales_request_status_change
      // trigger on the UPDATE above (no manual insert -> no duplicate row).

      toast({
        title: "Marcado como Test",
        description: "La nota de venta ha sido marcada como test.",
        variant: "default",
      });

      if (onOrderStatusChange) onOrderStatusChange();
      setSelectedOrder(null);
    } catch (error) {
      console.error("Error marking as test:", error);
      toast({
        title: "Error",
        description: "No se pudo marcar la nota como test.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Mark order as delivered (in_transit → delivered)
   */
  const markAsDelivered = async () => {
    if (!selectedOrder) return;

    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "delivered",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Trigger logs the change; just attach the reason to that row.
      await supabase.rpc("annotate_latest_status_change", {
        p_sales_request_id: selectedOrder.id,
        p_new_status: "delivered",
        p_comment: "Marcado como entregado manualmente",
      });

      toast({
        title: "Marcado como Entregado",
        description: "La nota de venta ha sido marcada como entregada.",
        variant: "default",
      });

      if (onOrderStatusChange) onOrderStatusChange();
      setSelectedOrder(null);
    } catch (error) {
      console.error("Error marking as delivered:", error);
      toast({
        title: "Error",
        description: "No se pudo marcar la nota como entregada.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Revert delivered order back to in_transit (superadmin only)
   */
  const revertToInTransit = async () => {
    if (!selectedOrder) return;

    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "in_transit",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Trigger logs the change; just attach the reason to that row.
      await supabase.rpc("annotate_latest_status_change", {
        p_sales_request_id: selectedOrder.id,
        p_new_status: "in_transit",
        p_comment: "Revertido a En Tránsito por superadmin",
      });

      toast({
        title: "Estado actualizado",
        description: "La nota de venta ha sido revertida a En Tránsito.",
      });

      if (onOrderStatusChange) onOrderStatusChange();
    } catch (error) {
      console.error("Error reverting to in_transit:", error);
      toast({
        title: "Error",
        description: "No se pudo revertir el estado de la nota de venta.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Force approve prepaid orders without validating payments (superadmin only)
   */
  const handleForceApproveWithoutPaymentValidation = async () => {
    if (!selectedOrder) return;

    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "accepted",
          approved_rejected_by: profileStore.id,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Trigger logs the change; just attach the reason to that row.
      await supabase.rpc("annotate_latest_status_change", {
        p_sales_request_id: selectedOrder.id,
        p_new_status: "accepted",
        p_comment: "Aprobado por superadmin sin validar pagos",
      });

      // Refresh local state
      const updatedOrder = await fetchAndTransformSalesRequest(selectedOrder.id);
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }

      toast({
        title: "Nota de venta aprobada",
        description: "Aprobada sin validar pagos (superadmin override)",
      });

      if (onOrderStatusChange) onOrderStatusChange();
    } catch (error) {
      console.error("Error force approving order:", error);
      toast({
        title: "Error",
        description: "No se pudo aprobar la nota de venta",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Mark order as paid
   * For payment_to_check and prepaid orders, this also approves them
   */
  const markAsPaid = async () => {
    if (!selectedOrder) return;

    setIsProcessing(true);
    try {
      // Prepare update data
      const updateData: any = {
        paid: true,
        updated_at: new Date().toISOString(),
      };

      // Approve orders that are in "payment_to_check" or "prepaid" status
      if (
        selectedOrder.status === "payment_to_check" ||
        selectedOrder.status === "prepaid"
      ) {
        updateData.status = "accepted";
        updateData.approved_rejected_by = profileStore.id;
      }

      const { error } = await supabase
        .from("sales_request")
        .update(updateData)
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Verify all unverified payments when marking as paid
      await verifyAllUnverifiedPayments(selectedOrder.id, profileStore.id);

      // Status change (if any) is logged by the trigger; attach the reason.
      if (
        selectedOrder.status === "payment_to_check" ||
        selectedOrder.status === "prepaid"
      ) {
        const comments =
          selectedOrder.status === "prepaid"
            ? "Aprobado al marcar como pagado desde TRANSFERENCIA ANTICIPADA"
            : "Aprobado al marcar como pagado desde validación de pago";

        await supabase.rpc("annotate_latest_status_change", {
          p_sales_request_id: selectedOrder.id,
          p_new_status: "accepted",
          p_comment: comments,
        });
      }

      // Refresh local state
      const updatedOrder = await fetchAndTransformSalesRequest(selectedOrder.id);
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }

      toast({
        title: "Marcado como Pagado",
        description:
          selectedOrder.status === "payment_to_check" ||
          selectedOrder.status === "prepaid"
            ? "La nota de venta ha sido marcada como pagada y aprobada."
            : "La nota de venta ha sido marcada como pagada.",
        variant: "default",
      });

      if (onOrderStatusChange) await onOrderStatusChange();
    } catch (error) {
      console.error("Error marking as paid:", error);
      toast({
        title: "Error",
        description: "No se pudo marcar la nota como pagada.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Mark order as unpaid
   */
  const markAsUnpaid = async () => {
    if (!selectedOrder) return;

    setIsProcessing(true);
    try {
      const updateData: any = {
        paid: false,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("sales_request")
        .update(updateData)
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Add status history entry
      await supabase.from("sales_request_status_history").insert({
        sales_request_id: selectedOrder.id,
        new_status: selectedOrder.status,
        changed_by: profileStore.id,
        changed_at: new Date().toISOString(),
        comments: "Marcado como NO pagado por administrador",
      });

      // Refresh local state
      const updatedOrder = await fetchAndTransformSalesRequest(selectedOrder.id);
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }

      if (onOrderStatusChange) onOrderStatusChange();

      toast({
        title: "Éxito",
        description: "Nota marcada como NO pagada correctamente.",
      });
    } catch (error) {
      console.error("Error marking as unpaid:", error);
      toast({
        title: "Error",
        description: "No se pudo marcar la nota como NO pagada.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle transportista rejection of in_transit orders
   */
  const handleTransportistaReject = async (reason: string) => {
    if (!selectedOrder || !reason.trim()) {
      toast({
        title: "Error",
        description: "Debe proporcionar un motivo para rechazar la nota",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "rejected",
          approved_rejected_by: profileStore.id,
          rejected_reason: reason.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Trigger logs the change; just attach the reason to that row.
      await supabase.rpc("annotate_latest_status_change", {
        p_sales_request_id: selectedOrder.id,
        p_new_status: "rejected",
        p_comment: `Rechazado por transportista: ${reason.trim()}`,
      });

      toast({
        title: "Nota rechazada",
        description: "La nota de venta ha sido rechazada correctamente",
        variant: "default",
      });

      if (onOrderStatusChange) onOrderStatusChange();
    } catch (error) {
      console.error("Error rejecting order:", error);
      toast({
        title: "Error",
        description: "No se pudo rechazar la nota de venta",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Approve document for invoicing (accepted → por_facturar)
   */
  const approveDocument = async () => {
    if (!selectedOrder) return;

    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "por_facturar",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Trigger logs the change; just attach the reason to that row.
      await supabase.rpc("annotate_latest_status_change", {
        p_sales_request_id: selectedOrder.id,
        p_new_status: "por_facturar",
        p_comment: "Documento aprobado para facturación",
      });

      // Create pick task for warehouse
      const { data: pickTaskData, error: pickTaskError } =
        await supabase.functions.invoke("create-pick-task", {
          body: { salesRequestId: selectedOrder.id },
        });

      if (pickTaskError) {
        console.error("Error creating pick task:", pickTaskError);
        toast({
          title: "Advertencia",
          description:
            "No se pudo crear la tarea de picking automaticamente. Contacte a soporte.",
          variant: "destructive",
        });
      } else if (pickTaskData?.error) {
        console.error("Pick task creation failed:", pickTaskData.error);
        toast({
          title: "Advertencia",
          description: pickTaskData.message || pickTaskData.error,
          variant: "destructive",
        });
      }

      toast({
        title: "Documento Aprobado",
        description: "La nota de venta ha sido enviada a Por Facturar.",
        variant: "default",
      });

      if (onOrderStatusChange) await onOrderStatusChange();
    } catch (error) {
      console.error("Error approving document:", error);
      toast({
        title: "Error",
        description: "No se pudo aprobar el documento.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    markAsTest,
    markAsDelivered,
    revertToInTransit,
    markAsPaid,
    markAsUnpaid,
    handleForceApproveWithoutPaymentValidation,
    handleTransportistaReject,
    approveDocument,
    isProcessing,
  };
}
